
import React, { useState } from "react";
import { useParams } from "react-router-dom";
import {
  useMutation,
  useQuery,
} from "convex/react";

import { api } from "./convex/_generated/api";

import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";

import "./ProductivityCareerPage.css";

export default function ProductivityCareerPage() {
  const { progressId, userId } = useParams();

  /*
   * ----------------------------------------
   * MUTATION
   * ----------------------------------------
   */

  const calculateProductivityCareer =
    useMutation(
      api.progress.calculateProductivityCareer
    );

  /*
   * ----------------------------------------
   * QUERY EXISTING RESULTS
   * ----------------------------------------
   */

  const usualresult = useQuery(
    api.progress.getProductivityCareerResults,
    progressId && userId
      ? {
          progressId,
          userId,
        }
      : "skip"
  );

  /*
   * ----------------------------------------
   * DAILY INPUT STATE
   * ----------------------------------------
   */

  const [focusMinutes, setFocusMinutes] =
    useState("");

  const [deepWorkMinutes, setDeepWorkMinutes] =
    useState("");

  const [
    taskCompletionRate,
    setTaskCompletionRate,
  ] = useState("");

  const [
    careerDevelopmentMinutes,
    setCareerDevelopmentMinutes,
  ] = useState("");

  /*
   * ----------------------------------------
   * UI STATE
   * ----------------------------------------
   */

  const [loading, setLoading] =
    useState(false);

  const [error, setError] =
    useState("");

  /*
   * ----------------------------------------
   * GET TODAY
   * ----------------------------------------
   */

  const getToday = () => {
    return new Date()
      .toISOString()
      .split("T")[0];
  };

  /*
   * ----------------------------------------
   * GET CURRENT WEEK
   * ----------------------------------------
   */

  const getCurrentWeek = () => {
    const today = new Date();

    const day = today.getDay();

    const diffToMonday =
      day === 0
        ? -6
        : 1 - day;

    const weekStart =
      new Date(today);

    weekStart.setDate(
      today.getDate() +
        diffToMonday
    );

    const weekEnd =
      new Date(weekStart);

    weekEnd.setDate(
      weekStart.getDate() + 6
    );

    const formatDate = (date) => {
      return date
        .toISOString()
        .split("T")[0];
    };

    return {
      weekStart:
        formatDate(weekStart),

      weekEnd:
        formatDate(weekEnd),
    };
  };

  /*
   * ----------------------------------------
   * SUBMIT DAILY PRODUCTIVITY
   * ----------------------------------------
   */

  const handleSubmit = async (e) => {
    e.preventDefault();

    setError("");

    if (!progressId || !userId) {
      setError(
        "Missing progress or user information."
      );

      return;
    }

    if (
      focusMinutes === "" ||
      deepWorkMinutes === "" ||
      taskCompletionRate === "" ||
      careerDevelopmentMinutes === ""
    ) {
      setError(
        "Please complete all fields."
      );

      return;
    }

    const focus =
      Number(focusMinutes);

    const deepWork =
      Number(deepWorkMinutes);

    const taskCompletion =
      Number(
        taskCompletionRate
      );

    const careerDevelopment =
      Number(
        careerDevelopmentMinutes
      );

    if (
      Number.isNaN(focus) ||
      Number.isNaN(deepWork) ||
      Number.isNaN(taskCompletion) ||
      Number.isNaN(
        careerDevelopment
      )
    ) {
      setError(
        "Please enter valid numbers."
      );

      return;
    }

    if (
      focus < 0 ||
      deepWork < 0 ||
      careerDevelopment < 0
    ) {
      setError(
        "Minutes cannot be negative."
      );

      return;
    }

    if (
      taskCompletion < 0 ||
      taskCompletion > 100
    ) {
      setError(
        "Task completion rate must be between 0 and 100."
      );

      return;
    }

    try {
      setLoading(true);

      const date =
        getToday();

      const {
        weekStart,
        weekEnd,
      } = getCurrentWeek();

      await calculateProductivityCareer({
        userId,
        progressId,

        date,

        focusMinutes:
          focus,

        deepWorkMinutes:
          deepWork,

        taskCompletionRate:
          taskCompletion,

        careerDevelopmentMinutes:
          careerDevelopment,

        weekStart,
        weekEnd,
      });

      /*
       * No setResult().
       *
       * The useQuery result updates
       * automatically after the mutation.
       */

    } catch (error) {
      console.error(error);

      setError(
        error?.message ||
          "Failed to calculate productivity score."
      );
    } finally {
      setLoading(false);
    }
  };

  /*
   * ----------------------------------------
   * DAILY RECORDS FROM CONVEX
   * ----------------------------------------
   */

  const dailyRecords =
    usualresult?.dailyRecords ?? [];

  /*
   * ----------------------------------------
   * WEEKLY RECORDS FROM CONVEX
   * ----------------------------------------
   */

  const weeklyRecords =
    usualresult?.weeklyRecords ?? [];

  /*
   * ----------------------------------------
   * SORT DAILY RECORDS
   * ----------------------------------------
   */

  const sortedDailyRecords =
    [...dailyRecords].sort(
      (a, b) =>
        a.date.localeCompare(
          b.date
        )
    );

  /*
   * ----------------------------------------
   * SORT WEEKLY RECORDS
   * ----------------------------------------
   */

  const sortedWeeklyRecords =
    [...weeklyRecords].sort(
      (a, b) =>
        a.weekStart.localeCompare(
          b.weekStart
        )
    );

  /*
   * ----------------------------------------
   * LATEST WEEKLY RECORD
   * ----------------------------------------
   */

  const latestWeeklyRecord =
    sortedWeeklyRecords.length > 0
      ? sortedWeeklyRecords[
          sortedWeeklyRecords.length - 1
        ]
      : null;

  /*
   * ----------------------------------------
   * DAILY CHART
   * USE STORED DAILY RECORDS
   * ----------------------------------------
   */

  const dailyChartData =
    sortedDailyRecords.map(
      (record) => ({
        date: record.date,

        dailyScore:
          Math.round(
            record.dailyScore
          ),
      })
    );

  /*
   * ----------------------------------------
   * WEEKLY CHART
   * USE STORED WEEKLY RECORDS
   * ----------------------------------------
   */

  const weeklyChartData =
    sortedWeeklyRecords.map(
      (record) => ({
        week:
          record.weekStart,

        trackingScore:
          Math.round(
            record.trackingScore
          ),

        activityScore:
          Math.round(
            record.activityScore
          ),

        finalScore:
          Math.round(
            record.finalScore
          ),
      })
    );

  return (
    <div className="productivity-page">
      <div className="productivity-container">

        {/* ========================================
            PAGE HEADER
            ======================================== */}

        <header className="productivity-header">

          <span className="productivity-eyebrow">
            PRODUCTIVITY & CAREER
          </span>

          <h1>
            Productivity & Career
          </h1>

          <p>
            Track today's productivity and career
            development.
          </p>

        </header>

        {/* ========================================
            FORM
            ======================================== */}

        <form
          onSubmit={handleSubmit}
          className="productivity-form"
        >

          <div className="form-header">
            <div className="form-icon">
              ↗
            </div>

            <div>
              <h2>
                Daily Productivity
              </h2>

              <p>
                Track your focus, deep work,
                tasks, and career development.
              </p>
            </div>
          </div>

          {/* ----------------------------------------
              FOCUS
              ---------------------------------------- */}

          <div className="input-group">

            <label>
              Focus Minutes
            </label>

            <input
              type="number"
              min="0"
              value={focusMinutes}
              onChange={(e) =>
                setFocusMinutes(
                  e.target.value
                )
              }
              placeholder="Example: 120"
            />

          </div>

          {/* ----------------------------------------
              DEEP WORK
              ---------------------------------------- */}

          <div className="input-group">

            <label>
              Deep Work Minutes
            </label>

            <input
              type="number"
              min="0"
              value={deepWorkMinutes}
              onChange={(e) =>
                setDeepWorkMinutes(
                  e.target.value
                )
              }
              placeholder="Example: 90"
            />

          </div>

          {/* ----------------------------------------
              TASK COMPLETION
              ---------------------------------------- */}

          <div className="input-group">

            <label>
              Task Completion Rate (%)
            </label>

            <input
              type="number"
              min="0"
              max="100"
              value={
                taskCompletionRate
              }
              onChange={(e) =>
                setTaskCompletionRate(
                  e.target.value
                )
              }
              placeholder="Example: 85"
            />

          </div>

          {/* ----------------------------------------
              CAREER DEVELOPMENT
              ---------------------------------------- */}

          <div className="input-group">

            <label>
              Career Development Minutes
            </label>

            <input
              type="number"
              min="0"
              value={
                careerDevelopmentMinutes
              }
              onChange={(e) =>
                setCareerDevelopmentMinutes(
                  e.target.value
                )
              }
              placeholder="Example: 30"
            />

          </div>

          {/* ----------------------------------------
              ERROR
              ---------------------------------------- */}

          {error && (
            <div className="productivity-error">
              <span>!</span>
              {error}
            </div>
          )}

          {/* ----------------------------------------
              SUBMIT
              ---------------------------------------- */}

          <button
            type="submit"
            disabled={loading}
            className="productivity-submit"
          >
            {loading
              ? "Saving..."
              : "Save Today's Productivity"}
          </button>

        </form>

        {/* ========================================
            RESULTS
            ======================================== */}

        {usualresult && (
          <div className="results-section">

            <div className="results-heading">
              <span>
                PERFORMANCE OVERVIEW
              </span>

              <h2>
                Productivity Results
              </h2>
            </div>

            {/* ======================================
                WEEKLY SCORE CARDS
                ====================================== */}

            {latestWeeklyRecord && (
              <div className="score-grid">

                {/* Tracking */}

                <div className="score-card green-card">

                  <span className="score-label">
                    TRACKING
                  </span>

                  <h3>
                    Tracking Score
                  </h3>

                  <p>
                    {Math.round(
                      latestWeeklyRecord.trackingScore
                    )}
                  </p>

                </div>

                {/* Activity */}

                <div className="score-card orange-card">

                  <span className="score-label">
                    ACTIVITY
                  </span>

                  <h3>
                    Activity Score
                  </h3>

                  <p>
                    {Math.round(
                      latestWeeklyRecord.activityScore
                    )}
                  </p>

                </div>

                {/* Final */}

                <div className="score-card lime-card">

                  <span className="score-label">
                    OVERALL
                  </span>

                  <h3>
                    Final Score
                  </h3>

                  <p>
                    {Math.round(
                      latestWeeklyRecord.finalScore
                    )}
                  </p>

                </div>

                {/* Days */}

                <div className="score-card neutral-card">

                  <span className="score-label">
                    CONSISTENCY
                  </span>

                  <h3>
                    Days Measured
                  </h3>

                  <p>
                    {
                      latestWeeklyRecord.daysMeasured
                    }
                  </p>

                </div>

                {/* Activities */}

                <div className="score-card neutral-card">

                  <span className="score-label">
                    GUIDANCE
                  </span>

                  <h3>
                    Suggestions
                  </h3>

                  <p>
                    {
                      latestWeeklyRecord.suggestionCount
                    }
                  </p>

                </div>

              </div>
            )}

            {/* ======================================
                DAILY RECORDS
                ====================================== */}

            <div className="content-card records-card">

              <div className="content-header">
                <div>
                  <span>
                    HISTORY
                  </span>

                  <h2>
                    Daily Productivity Records
                  </h2>

                  <p>
                    All saved daily productivity records.
                  </p>
                </div>
              </div>

              {sortedDailyRecords.length ===
              0 ? (
                <p className="empty-message">
                  No daily records yet.
                </p>
              ) : (
                <div className="table-wrapper">

                  <table className="productivity-table">

                    <thead>
                      <tr>

                        <th>
                          Date
                        </th>

                        <th>
                          Day
                        </th>

                        <th>
                          Focus
                        </th>

                        <th>
                          Deep Work
                        </th>

                        <th>
                          Task %
                        </th>

                        <th>
                          Career
                        </th>

                        <th>
                          Score
                        </th>

                      </tr>
                    </thead>

                    <tbody>
                      {sortedDailyRecords.map(
                        (record) => (
                          <tr
                            key={
                              record._id
                            }
                          >

                            <td>
                              {
                                record.date
                              }
                            </td>

                            <td>
                              {
                                record.day
                              }
                            </td>

                            <td>
                              {
                                record.focusMinutes
                              }{" "}
                              min
                            </td>

                            <td>
                              {
                                record.deepWorkMinutes
                              }{" "}
                              min
                            </td>

                            <td>
                              {
                                record.taskCompletionRate
                              }
                              %
                            </td>

                            <td>
                              {
                                record.careerDevelopmentMinutes
                              }{" "}
                              min
                            </td>

                            <td className="table-score">
                              {Math.round(
                                record.dailyScore
                              )}
                            </td>

                          </tr>
                        )
                      )}
                    </tbody>

                  </table>

                </div>
              )}

            </div>

            {/* ======================================
                CHART 1
                ALL DAILY SCORES
                ====================================== */}

            {dailyChartData.length > 0 && (
              <div className="content-card chart-card">

                <div className="chart-header">
                  <div>

                    <span>
                      DAILY PERFORMANCE
                    </span>

                    <h2>
                      Daily Productivity Scores
                    </h2>

                    <p>
                      Productivity score for every saved day.
                    </p>

                  </div>

                  <div className="chart-badge">
                    0 — 100
                  </div>
                </div>

                <div className="chart-container">

                  <ResponsiveContainer
                    width="100%"
                    height="100%"
                  >
                    <BarChart
                      data={
                        dailyChartData
                      }
                    >

                      <CartesianGrid
                        strokeDasharray="3 3"
                        stroke="#26372d"
                      />

                      <XAxis
                        dataKey="date"
                        tick={{
                          fill: "#94a3b8",
                          fontSize: 12,
                        }}
                        axisLine={{
                          stroke: "#33443a",
                        }}
                        tickLine={false}
                      />

                      <YAxis
                        domain={[
                          0,
                          100,
                        ]}
                        tick={{
                          fill: "#94a3b8",
                          fontSize: 12,
                        }}
                        axisLine={false}
                        tickLine={false}
                      />

                      <Tooltip
                        contentStyle={{
                          background:
                            "#101713",
                          border:
                            "1px solid #35503e",
                          borderRadius:
                            "12px",
                          color:
                            "#ffffff",
                        }}
                      />

                      <Legend />

                      <Bar
                        dataKey="dailyScore"
                        name="Daily Score"
                        fill="#22c55e"
                        radius={[
                          7,
                          7,
                          0,
                          0,
                        ]}
                      />

                    </BarChart>
                  </ResponsiveContainer>

                </div>

              </div>
            )}

            {/* ======================================
                CHART 2
                ALL WEEKLY SCORES
                ====================================== */}

            {weeklyChartData.length > 0 && (
              <div className="content-card chart-card">

                <div className="chart-header">
                  <div>

                    <span>
                      WEEKLY PERFORMANCE
                    </span>

                    <h2>
                      Weekly Productivity Scores
                    </h2>

                    <p>
                      Tracking, activity, and final scores for every saved week.
                    </p>

                  </div>

                  <div className="chart-badge">
                    0 — 100
                  </div>
                </div>

                <div className="chart-container">

                  <ResponsiveContainer
                    width="100%"
                    height="100%"
                  >
                    <BarChart
                      data={
                        weeklyChartData
                      }
                    >

                      <CartesianGrid
                        strokeDasharray="3 3"
                        stroke="#26372d"
                      />

                      <XAxis
                        dataKey="week"
                        tick={{
                          fill: "#94a3b8",
                          fontSize: 12,
                        }}
                        axisLine={{
                          stroke: "#33443a",
                        }}
                        tickLine={false}
                      />

                      <YAxis
                        domain={[
                          0,
                          100,
                        ]}
                        tick={{
                          fill: "#94a3b8",
                          fontSize: 12,
                        }}
                        axisLine={false}
                        tickLine={false}
                      />

                      <Tooltip
                        contentStyle={{
                          background:
                            "#101713",
                          border:
                            "1px solid #35503e",
                          borderRadius:
                            "12px",
                          color:
                            "#ffffff",
                        }}
                      />

                      <Legend />

                      <Bar
                        dataKey="trackingScore"
                        name="Tracking Score"
                        fill="#22c55e"
                        radius={[
                          6,
                          6,
                          0,
                          0,
                        ]}
                      />

                      <Bar
                        dataKey="activityScore"
                        name="Activity Score"
                        fill="#f97316"
                        radius={[
                          6,
                          6,
                          0,
                          0,
                        ]}
                      />

                      <Bar
                        dataKey="finalScore"
                        name="Final Score"
                        fill="#84cc16"
                        radius={[
                          6,
                          6,
                          0,
                          0,
                        ]}
                      />

                    </BarChart>
                  </ResponsiveContainer>

                </div>

              </div>
            )}

            {/* ======================================
                WEEKLY DETAILS
                ====================================== */}

            {latestWeeklyRecord && (
              <div className="content-card weekly-details-card">

                <div className="content-header">

                  <div>
                    <span>
                      WEEKLY BREAKDOWN
                    </span>

                    <h2>
                      Latest Weekly Details
                    </h2>
                  </div>

                </div>

                <div className="details-grid">

                  <p>
                    <strong>
                      Week Start:
                    </strong>{" "}
                    {
                      latestWeeklyRecord.weekStart
                    }
                  </p>

                  <p>
                    <strong>
                      Week End:
                    </strong>{" "}
                    {
                      latestWeeklyRecord.weekEnd
                    }
                  </p>

                  <p>
                    <strong>
                      Average Focus:
                    </strong>{" "}
                    {Math.round(
                      latestWeeklyRecord.averageFocusMinutes
                    )}{" "}
                    min/day
                  </p>

                  <p>
                    <strong>
                      Average Deep Work:
                    </strong>{" "}
                    {Math.round(
                      latestWeeklyRecord.averageDeepWorkMinutes
                    )}{" "}
                    min/day
                  </p>

                  <p>
                    <strong>
                      Average Task Completion:
                    </strong>{" "}
                    {Math.round(
                      latestWeeklyRecord.averageTaskCompletionRate
                    )}
                    %
                  </p>

                  <p>
                    <strong>
                      Average Career Development:
                    </strong>{" "}
                    {Math.round(
                      latestWeeklyRecord.averageCareerDevelopmentMinutes
                    )}{" "}
                    min/day
                  </p>

                  <p>
                    <strong>
                      Tracking Score:
                    </strong>{" "}
                    {Math.round(
                      latestWeeklyRecord.trackingScore
                    )}
                  </p>

                  <p>
                    <strong>
                      Activity Score:
                    </strong>{" "}
                    {Math.round(
                      latestWeeklyRecord.activityScore
                    )}
                  </p>

                  <p>
                    <strong>
                      Final Score:
                    </strong>{" "}
                    {Math.round(
                      latestWeeklyRecord.finalScore
                    )}
                  </p>

                  <p>
                    <strong>
                      Suggestions:
                    </strong>{" "}
                    {
                      latestWeeklyRecord.suggestionCount
                    }
                  </p>

                </div>

              </div>
            )}

          </div>
        )}

      </div>
    </div>
  );
}
